import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {searchCompleted} from 'modules/sapiSearch/slice'
import {syncStoreWithUrl} from 'modules/search/slice'

export type ChatView = 'chat' | 'history'

export type ChatFocus = {type: 'hotel'; hotelId: string} | {type: 'search'}

interface ChatState {
  isOpen: boolean
  view: ChatView
  pageFocus: ChatFocus | null
  focus: ChatFocus | null
  sessionId: string | null
  sentSuggestions: string[]
}

const initialState: ChatState = {
  isOpen: false,
  view: 'chat',
  pageFocus: null,
  focus: null,
  sessionId: null,
  sentSuggestions: []
}

function applyPageFocus(state: ChatState, next: ChatFocus | null) {
  const prev = state.pageFocus
  if (
    prev?.type === next?.type &&
    (next?.type !== 'hotel' ||
      (prev?.type === 'hotel' && prev.hotelId === next.hotelId))
  ) {
    return
  }
  state.pageFocus = next
  state.focus = next
  state.sentSuggestions = []
}

const {actions, reducer} = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    openChat: state => {
      state.isOpen = true
    },
    closeChat: state => {
      state.isOpen = false
      state.view = 'chat'
    },
    setView: (state, action: PayloadAction<ChatView>) => {
      state.view = action.payload
    },
    setFocus: (state, action: PayloadAction<ChatFocus | null>) => {
      state.focus = action.payload
      state.sentSuggestions = []
    },
    setSessionId: (state, action: PayloadAction<string | null>) => {
      state.sessionId = action.payload
    },
    startNewChat: state => {
      state.sessionId = null
      state.focus = state.pageFocus
      state.view = 'chat'
      state.sentSuggestions = []
    },
    markSuggestionSent: (state, action: PayloadAction<string>) => {
      if (!state.sentSuggestions.includes(action.payload)) {
        state.sentSuggestions.push(action.payload)
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(searchCompleted, state => {
        applyPageFocus(state, {type: 'search'})
      })
      .addCase(syncStoreWithUrl, (state, action) => {
        const {hotelId} = action.payload.urlParams
        if (hotelId) {
          applyPageFocus(state, {type: 'hotel', hotelId})
        } else if (state.pageFocus?.type === 'hotel') {
          applyPageFocus(state, {type: 'search'})
        }
      })
  }
})

export const {
  openChat,
  closeChat,
  setView,
  setFocus,
  setSessionId,
  startNewChat,
  markSuggestionSent
} = actions

export default reducer
